import { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import type { Artifact } from '@/types';
import { cn } from '@/lib/utils';

interface CodeBlockProps {
  artifact: Artifact;
}

export function CodeBlock({ artifact }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const language = artifact.language ?? artifact.metadata?.language as string ?? 'text';
  const lines = artifact.content.split('\n');
  const isLong = lines.length > 20;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-300">
            {language}
          </span>
          {artifact.title && (
            <span className="text-sm font-medium text-gray-100">{artifact.title}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLong && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
            >
              {collapsed ? (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Expand
                </>
              ) : (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Collapse
                </>
              )}
            </button>
          )}
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
            title="Copy code"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-green-400" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Code */}
      <div className={cn('overflow-x-auto', collapsed && 'max-h-40 overflow-y-hidden')}>
        <pre className="p-4 text-sm text-gray-100">
          <code className={`language-${language}`}>
            {collapsed ? lines.slice(0, 10).join('\n') + '\n...' : artifact.content}
          </code>
        </pre>
      </div>

      {/* Collapsed indicator */}
      {collapsed && (
        <div className="border-t border-gray-700 px-4 py-2 text-center text-xs text-gray-400">
          {lines.length - 10} more lines hidden
        </div>
      )}
    </div>
  );
}
